import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Grid,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  AlertTitle,
  Skeleton,
  Tooltip,
  Stack,
  Divider,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Button,
  TableSortLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  Block as DeactivateIcon,
  Security as SecurityIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Map as MapIcon,
  MyLocation as MyLocationIcon,
  Check as CheckIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import CButton from "../../../basecomp/components/CButton";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CDropDown from "../../../basecomp/components/CDropDown";
import { Autocomplete } from "@mui/material";
import MyGLNsVM from "../viewmodel/MyGLNsVM";

// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map picker component
const MapPicker = ({ position, onPositionChange, height = 300 }) => {
  const LocationMarker = () => {
    useMapEvents({
      click(e) {
        onPositionChange([e.latlng.lat, e.latlng.lng]);
      },
    });

    return position === null ? null : (
      <Marker position={position}>
        <Popup>
          Selected Location<br />
          Lat: {position[0].toFixed(6)}<br />
          Lng: {position[1].toFixed(6)}
        </Popup>
      </Marker>
    );
  };

  return (
    <Box sx={{ height, width: '100%', border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <MapContainer
        center={position || [20.5937, 78.9629]}
        zoom={position ? 13 : 5}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <LocationMarker />
      </MapContainer>
    </Box>
  );
};

const MyGLNsView = ({ onSwitchToCreate }) => {
  const navigate = useNavigate();

  const {
    isLoading,
    userProfile,
    glnRecords,
    filteredRecords,
    paginatedRecords,
    totalRecords,
    currentPage,
    recordsPerPage,
    recordsByCategory,
    lastUpdated,
    selectedCategory,
    searchQuery,
    selectedRecords,
    formData,
    sortBy,
    sortDirection,

    handleCategoryChange,
    handleSearchChange,
    handleRecordSelection,
    handleBulkSelection,
    handlePageChange,
    handleEditRecord,
    handleDeactivateRecord,
    handleFormChange,
    resetFormData,
    loadRecordToForm,
    fetchGlnRecords,
    handleExportRecords,
    handleSortChange,
    handleBulkDeactivate,
    handleRecordsPerPageChange,

    canEdit,
    canCreate,
    canDeactivate,
    canView,

    formatDate,
    formatLastUpdated,
    countries,
    handleCodeChange
  } = MyGLNsVM();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('edit');
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapPosition, setMapPosition] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [recordToDeactivate, setRecordToDeactivate] = useState(null);

  const isRecordActive = (record) => {
    if (!record || !record.gln_status) return false;
    const status = record.gln_status;
    return status.toLowerCase() === 'active' || status.toUpperCase() === 'A';
  };

  const handleMenuOpen = (event, record) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRecord(null);
  };

  const openEditDialog = (record) => {
    setDialogMode('edit');
    loadRecordToForm(record);
    setSelectedRecord(record);
    setMapPosition(
      record.latitude && record.longitude
        ? [parseFloat(record.latitude), parseFloat(record.longitude)]
        : null
    );
    setDialogOpen(true);
  };

  const openViewDialog = (record) => {
    setDialogMode('view');
    setSelectedRecord(record);
    setMapPosition(
      record.latitude && record.longitude
        ? [parseFloat(record.latitude), parseFloat(record.longitude)]
        : null
    );
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRecord(null);
    resetFormData();
    setMapPosition(null);
  };

  const openMapDialog = () => {
    setMapDialogOpen(true);
  };

  const closeMapDialog = () => {
    setMapDialogOpen(false);
  };

  const handleMapConfirm = () => {
    if (mapPosition) {
      handleFormChange('latitude', mapPosition[0].toFixed(6));
      handleFormChange('longitude', mapPosition[1].toFixed(6));
    }
    closeMapDialog();
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPosition = [position.coords.latitude, position.coords.longitude];
          setMapPosition(newPosition);
          handleFormChange('latitude', position.coords.latitude.toFixed(6));
          handleFormChange('longitude', position.coords.longitude.toFixed(6));
        },
        (error) => {
          console.error('Error getting location:', error);
        }
      );
    }
  };

  const handleSaveRecord = async () => {
    try {
      if (dialogMode === 'edit') {
        await handleEditRecord(selectedRecord.gln_id, formData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving record:', error);
    }
  };

  const handleViewRecordClick = (glnId) => {
    navigate(`/global-location-data/main?glnid=${glnId}`);
  };

  const handleDeactivate = (recordId) => {
    const record = filteredRecords.find(r => r.gln_id === recordId);
    setRecordToDeactivate(record);
    setDeactivateDialogOpen(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!recordToDeactivate) return;

    try {
      await handleDeactivateRecord(recordToDeactivate.gln_id);
      setDeactivateDialogOpen(false);
      setRecordToDeactivate(null);
    } catch (error) {
      console.error('Error deactivating record:', error);
    }
  };

  const handleCancelDeactivate = () => {
    setDeactivateDialogOpen(false);
    setRecordToDeactivate(null);
  };

  const handleExport = async () => {
    try {
      await handleExportRecords(filteredRecords, selectedCategory);
    } catch (error) {
      console.error('Error exporting records:', error);
    }
  };

  const handleExportSingleRecord = async (recordId) => {
    try {
      await handleExportRecords([recordId], 'single_record', true);
    } catch (error) {
      console.error('Error exporting single record:', error);
    }
  };

  const createSortHandler = (property) => (event) => {
    handleSortChange(property);
  };

  const categoryConfig = [
    {
      key: "myCompanyRecords",
      label: "My Company's GLNs",
      count: recordsByCategory.myCompanyRecords,
    },
    {
      key: "createdByMe",
      label: "Created by Me",
      count: recordsByCategory.createdByMe,
    },
  ];

  // Show loading while permissions are being fetched
  if (isLoading && !userProfile) {
    return (
      <Grid container spacing={3} sx={{ padding: "2em" }}>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CBackDrop cOpen={true} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  // Only show access denied after permissions have loaded
  if (userProfile && !canView()) {
    return (
      <Grid container spacing={3} sx={{ padding: "2em" }}>
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <AlertTitle>Access Denied</AlertTitle>
            <Typography>
              You don't have permission to view GLN records. Please contact your administrator.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <CBackDrop cOpen={isLoading} />
      <Grid container spacing={3} sx={{ padding: "2em", pt: 4, maxWidth: 1400, mx: "auto" }}>

        {/* Header Card */}
        <Grid item xs={12}>
          <Card
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 2,
              border: "1px solid #e5e7eb",
              backgroundColor: "#ffffff"
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5, color: "#111827" }}>
                  GLN Records Management
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  {userProfile?.role} • {userProfile?.company_name}
                </Typography>
              </Box>
              <Stack direction="row" spacing={3} alignItems="center">
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight={600} sx={{ lineHeight: 1, color: "#111827" }}>
                    {totalRecords}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                    Total Records
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ height: 40 }} />
                <Tooltip title="Refresh data">
                  <IconButton
                    onClick={fetchGlnRecords}
                    sx={{
                      color: "#3b82f6",
                      "&:hover": {
                        backgroundColor: "#eff6ff"
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Card>
        </Grid>

        {/* Permissions Display */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #e5e7eb",
            backgroundColor: "#fafafa"
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <SecurityIcon sx={{ color: "#6b7280" }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500} sx={{ color: "#374151", mb: 0.5 }}>
                  Your Permissions
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {canCreate() && (
                    <Chip label="Create" size="small" sx={{ backgroundColor: "#e0e7ff", color: "#3730a3", fontWeight: 500 }} />
                  )}
                  {canEdit() && (
                    <Chip label="Edit" size="small" sx={{ backgroundColor: "#dbeafe", color: "#1e40af", fontWeight: 500 }} />
                  )}
                  {canDeactivate() && (
                    <Chip label="Deactivate" size="small" sx={{ backgroundColor: "#fef3c7", color: "#92400e", fontWeight: 500 }} />
                  )}
                  {canView() && (
                    <Chip label="View" size="small" sx={{ backgroundColor: "#f3f4f6", color: "#374151", fontWeight: 500 }} />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Card>
        </Grid>

        {/* Category Filter Cards */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight={500} sx={{ mb: 1.5, color: "#374151" }}>
            Filter by Category
          </Typography>
          <Grid container spacing={2}>
            {categoryConfig.map((category) => {
              const isSelected = selectedCategory === category.key;

              return (
                <Grid item xs={12} sm={6} key={category.key}>
                  <Card
                    elevation={0}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: 2,
                      border: isSelected ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                      backgroundColor: isSelected ? "#eff6ff" : "white",
                      transition: "all 0.2s",
                      "&:hover": {
                        borderColor: "#3b82f6",
                        backgroundColor: "#f8fafc"
                      }
                    }}
                    onClick={() => handleCategoryChange(category.key)}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={500} sx={{ color: isSelected ? "#1e40af" : "#374151" }}>
                        {category.label}
                      </Typography>
                      <Chip
                        label={category.count || 0}
                        size="small"
                        sx={{
                          backgroundColor: isSelected ? "#3b82f6" : "#f3f4f6",
                          color: isSelected ? "white" : "#6b7280",
                          fontWeight: 600,
                          minWidth: 32
                        }}
                      />
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Search and Actions */}
        <Grid item xs={12}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              placeholder="Search by GLN ID, Code, Facility Name..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              variant="outlined"
              size="medium"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "white",
                  "&:hover fieldset": {
                    borderColor: "#3b82f6"
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3b82f6"
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              onClick={handleExport}
              startIcon={<ExportIcon />}
              variant="contained"
              sx={{
                borderRadius: 2,
                backgroundColor: "#3b82f6",
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                "&:hover": {
                  backgroundColor: "#2563eb"
                }
              }}
            >
              Export
            </Button>
          </Stack>
        </Grid>

        {/* GLN Records Table */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #e5e7eb" }}>
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: "#f9fafb",
                    "& .MuiTableCell-head": {
                      borderBottom: "1px solid #e5e7eb",
                      py: 2,
                      color: "#374151",
                      fontWeight: 600,
                      fontSize: "0.875rem"
                    }
                  }}>
                    <TableCell>GLN ID</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'generated_code'}
                        direction={sortBy === 'generated_code' ? sortDirection : 'asc'}
                        onClick={createSortHandler('generated_code')}
                      >
                        Code & Location
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'code_type'}
                        direction={sortBy === 'code_type' ? sortDirection : 'asc'}
                        onClick={createSortHandler('code_type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'updated_date'}
                        direction={sortBy === 'updated_date' ? sortDirection : 'asc'}
                        onClick={createSortHandler('updated_date')}
                      >
                        Updated
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell><Skeleton width={120} /></TableCell>
                        <TableCell><Skeleton width={200} /></TableCell>
                        <TableCell><Skeleton width={80} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                        <TableCell><Skeleton width={60} /></TableCell>
                        <TableCell><Skeleton width={100} /></TableCell>
                        <TableCell><Skeleton width={120} /></TableCell>
                      </TableRow>
                    ))
                  ) : paginatedRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Stack spacing={2} alignItems="center">
                          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db" }} />
                          <Typography variant="h6" color="text.secondary" fontWeight={500}>
                            No GLN records found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Try adjusting your filters or search criteria
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRecords.map((record, index) => (
                      <TableRow
                        key={`${record.gln_id}-${index}`}
                      
                        sx={{
                          "&:hover": { backgroundColor: "#fafbfc" },
                          "& .MuiTableCell-root": {
                            py: 2,
                            borderBottom: "1px solid #f3f4f6"
                          }
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} sx={{ color: "#111827" }}>
                            {record.gln_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight={500} sx={{ color: "#111827" }}>
                              {record.generated_code}
                            </Typography>
                            {record.facility_name && (
                              <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.875rem" }}>
                                {record.facility_name}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.code_type}
                            size="small"
                            sx={{
                              backgroundColor: "#f3f4f6",
                              color: "#374151",
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#374151" }}>
                            {record.created_by_user ? "You" : record.created_by_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={isRecordActive(record) ? "Active" : "Inactive"}
                            size="small"
                            sx={{
                              backgroundColor: isRecordActive(record) ? '#dcfce7' : '#fee2e2',
                              color: isRecordActive(record) ? '#166534' : '#991b1b',
                              fontWeight: 500
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "#6b7280" }}>
                            {formatDate(record.updated_date || record.created_date)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewRecordClick(record.gln_id)}
                                sx={{ color: "#3b82f6", "&:hover": { backgroundColor: "#eff6ff" } }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            {canEdit() && isRecordActive(record) && (record.created_by_user || userProfile?.role === 'Admin') && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => openEditDialog(record)}
                                  sx={{ color: "#059669", "&:hover": { backgroundColor: "#ecfdf5" } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {canDeactivate() && isRecordActive(record) && (record.created_by_user || userProfile?.role === 'Admin') && (
                              <Tooltip title="Deactivate">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeactivate(record.gln_id)}
                                  sx={{ color: "#dc2626", "&:hover": { backgroundColor: "#fee2e2" } }}
                                >
                                  <DeactivateIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip title="More">
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, record)}
                                sx={{ color: "#6b7280", "&:hover": { backgroundColor: "#f3f4f6" } }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination with Records Per Page */}
            {filteredRecords.length > 0 && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={2}
                sx={{ borderTop: "1px solid #f3f4f6" }}
              >
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Per page</InputLabel>
                  <Select
                    value={recordsPerPage}
                    onChange={(e) => handleRecordsPerPageChange(e.target.value)}
                    label="Per page"
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={15}>15</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>

                <Pagination
                  count={Math.ceil(filteredRecords.length / recordsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 1,
                      "&.Mui-selected": {
                        backgroundColor: "#3b82f6",
                        color: "white",
                        "&:hover": {
                          backgroundColor: "#2563eb"
                        }
                      }
                    }
                  }}
                />

                <Typography variant="body2" color="text.secondary">
                  Showing {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, filteredRecords.length)} of {filteredRecords.length}
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        {/* Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: { borderRadius: 2, minWidth: 180 }
          }}
        >
          <MenuItem onClick={() => {
            handleExportSingleRecord(selectedRecord?.gln_id);
            handleMenuClose();
          }}>
            <ExportIcon fontSize="small" sx={{ mr: 1.5, color: "#3b82f6" }} />
            <Typography variant="body2">Export Record</Typography>
          </MenuItem>
        </Menu>
      </Grid>

      {/* Edit/View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e5e7eb", py: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ color: "#111827" }}>
            {dialogMode === 'edit' ? 'Edit' : 'View'} GLN Record
            {selectedRecord && (
              <Typography component="span" sx={{ color: "#3b82f6", fontWeight: 500, ml: 1 }}>
                ({selectedRecord.gln_id})
              </Typography>
            )}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {selectedRecord && (
            <Box sx={{
              mb: 4,
              p: 2.5,
              backgroundColor: '#eff6ff',
              border: '2px solid #3b82f6',
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="h5" sx={{ color: '#1e40af', fontWeight: 600, mb: 0.5 }}>
                GLN ID: {selectedRecord.gln_id}
              </Typography>
              <Typography variant="body2" sx={{ color: '#3b82f6' }}>
                {dialogMode === 'edit' ? 'Editing this Global Location Number' : 'Viewing this Global Location Number'}
              </Typography>
            </Box>
          )}

          <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} md={5}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1e40af', fontWeight: 600, borderBottom: '2px solid #3b82f6', pb: 1, mb: 2 }}>
                    Frequently Updated
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Facility Name"
                    value={formData.facility_name || ''}
                    onChange={(e) => handleFormChange('facility_name', e.target.value)}
                    disabled={dialogMode === 'view'}
                    helperText="Name of the facility or organization"
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#3b82f6' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Latitude"
                    type="number"
                    value={formData.latitude || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        handleFormChange('latitude', value);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= -90 && numValue <= 90) {
                          handleFormChange('latitude', numValue.toString());
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Format to 6 decimal places on blur
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= -90 && value <= 90) {
                        handleFormChange('latitude', value.toFixed(6));
                      }
                    }}
                    disabled={dialogMode === 'view'}
                    inputProps={{
                      step: 'any',
                      min: -90,
                      max: 90
                    }}
                    helperText="Decimal degrees (-90 to 90, 6 decimal places)"
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#3b82f6' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Longitude"
                    type="number"
                    value={formData.longitude || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || value === '-') {
                        handleFormChange('longitude', value);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= -180 && numValue <= 180) {
                          handleFormChange('longitude', numValue.toString());
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Format to 6 decimal places on blur
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= -180 && value <= 180) {
                        handleFormChange('longitude', value.toFixed(6));
                      }
                    }}
                    disabled={dialogMode === 'view'}
                    inputProps={{
                      step: 'any',
                      min: -180,
                      max: 180
                    }}
                    helperText="Decimal degrees (-180 to 180, 6 decimal places)"
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: '#3b82f6' },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <FormControl fullWidth disabled={dialogMode === 'view'}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.gln_status || ''}
                      onChange={(e) => handleFormChange('gln_status', e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                    <FormHelperText>Current status of this GLN</FormHelperText>
                  </FormControl>
                </Grid>


                <Grid item xs={6}>
                  <FormControl fullWidth disabled={dialogMode === 'view'}>
                    <InputLabel>Country</InputLabel>
                    <Select
                      value={formData.country || ''}
                      onChange={(e) => handleFormChange('country', e.target.value)}
                      label="Country"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d1d5db',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Country</em>
                      </MenuItem>
                      {countries.map((country) => (
                        <MenuItem key={country.country_code} value={country.country_code}>
                          {country.country_name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      {dialogMode === 'view'
                        ? 'Country associated with this GLN'
                        : formData.code_type === 'UNLOCODE'
                          ? 'Auto-populated from UNLOCODE (first 2 characters)'
                          : 'Select the country for this GLN'}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth disabled={dialogMode === 'view'}>
                    <InputLabel>Role Type</InputLabel>
                    <Select
                      value={formData.role_type || ''}
                      onChange={(e) => handleFormChange('role_type', e.target.value)}
                      label="Role Type"
                    >
                      <MenuItem value="">Select Role Type</MenuItem>
                      <MenuItem value="Seaport">Seaport</MenuItem>
                      <MenuItem value="Rail Terminal">Rail Terminal</MenuItem>
                      <MenuItem value="Road Terminal">Road Terminal</MenuItem>
                      <MenuItem value="Airport">Airport</MenuItem>
                      <MenuItem value="Postal Exchange">Postal Exchange</MenuItem>
                      <MenuItem value="Intermodal Terminal">Intermodal Terminal</MenuItem>
                      <MenuItem value="Fixed Transport Function">Fixed Transport Function</MenuItem>
                      <MenuItem value="Border Crossing Function">Border Crossing Function</MenuItem>
                    </Select>
                    <FormHelperText>Specify the primary function of this location</FormHelperText>
                  </FormControl>
                </Grid>

                {dialogMode !== 'view' && (
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} mt={1}>
                      <Button
                        variant="contained"
                        startIcon={<MapIcon />}
                        onClick={openMapDialog}
                        size="small"
                        sx={{ backgroundColor: '#3b82f6', '&:hover': { backgroundColor: '#2563eb' } }}
                      >
                        Pick on Map
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<MyLocationIcon />}
                        onClick={getCurrentLocation}
                        size="small"
                        sx={{ borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { borderColor: '#2563eb', backgroundColor: '#eff6ff' } }}
                      >
                        Current Location
                      </Button>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#6b7280', fontWeight: 600, borderBottom: '2px solid #d1d5db', pb: 1, mb: 2 }}>
                    Additional Information
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#374151' }}>
                    GLN Type
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControl component="fieldset" disabled={dialogMode === 'view'}>
                      <Box>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: dialogMode === 'view' ? 'default' : 'pointer', padding: '6px 0' }}>
                          <input
                            type="checkbox"
                            checked={formData.gln_type?.includes('digital') || false}
                            onChange={(e) => {
                              if (dialogMode === 'view') return;
                              const currentTypes = formData.gln_type ? formData.gln_type.split(',').map(t => t.trim()) : [];
                              if (e.target.checked) {
                                if (!currentTypes.includes('digital')) {
                                  handleFormChange('gln_type', [...currentTypes, 'digital'].join(','));
                                }
                              } else {
                                handleFormChange('gln_type', currentTypes.filter(t => t !== 'digital').join(','));
                              }
                            }}
                            disabled={dialogMode === 'view'}
                            style={{ marginRight: '8px', accentColor: '#3b82f6' }}
                          />
                          <Typography variant="body2">Digital</Typography>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: dialogMode === 'view' ? 'default' : 'pointer', padding: '6px 0' }}>
                          <input
                            type="checkbox"
                            checked={formData.gln_type?.includes('mobile') || false}
                            onChange={(e) => {
                              if (dialogMode === 'view') return;
                              const currentTypes = formData.gln_type ? formData.gln_type.split(',').map(t => t.trim()) : [];
                              if (e.target.checked) {
                                if (!currentTypes.includes('mobile')) {
                                  handleFormChange('gln_type', [...currentTypes, 'mobile'].join(','));
                                }
                              } else {
                                handleFormChange('gln_type', currentTypes.filter(t => t !== 'mobile').join(','));
                              }
                            }}
                            disabled={dialogMode === 'view'}
                            style={{ marginRight: '8px', accentColor: '#3b82f6' }}
                          />
                          <Typography variant="body2">Mobile</Typography>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: dialogMode === 'view' ? 'default' : 'pointer', padding: '6px 0' }}>
                          <input
                            type="checkbox"
                            checked={formData.gln_type?.includes('fixed_physical_location') || false}
                            onChange={(e) => {
                              if (dialogMode === 'view') return;
                              const currentTypes = formData.gln_type ? formData.gln_type.split(',').map(t => t.trim()) : [];
                              if (e.target.checked) {
                                if (!currentTypes.includes('fixed_physical_location')) {
                                  handleFormChange('gln_type', [...currentTypes, 'fixed_physical_location'].join(','));
                                }
                              } else {
                                handleFormChange('gln_type', currentTypes.filter(t => t !== 'fixed_physical_location').join(','));
                              }
                            }}
                            disabled={dialogMode === 'view'}
                            style={{ marginRight: '8px', accentColor: '#3b82f6' }}
                          />
                          <Typography variant="body2">Fixed Physical Location</Typography>
                        </label>
                      </Box>
                      <FormHelperText>Select applicable GLN types</FormHelperText>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <CDropDown
                    cId="codeTypeField"
                    cHelperText="Code Type"
                    cData={["BIC", "IATA", "Port Facility Code", "UNLOCODE", "SMDG", "Other"]}
                    cOnChange={(value) => handleFormChange('code_type', value)}
                    cFullWidth={true}
                    cDefaultValue={formData.code_type}
                    cDisabled={dialogMode === 'view'}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ height: '100%', minHeight: 600 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1e40af', fontWeight: 600, borderBottom: '2px solid #3b82f6', pb: 1, mb: 2 }}>
                  Location Preview
                </Typography>
                {(formData.latitude && formData.longitude) ? (
                  <MapPicker
                    position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                    onPositionChange={(pos) => {
                      if (dialogMode !== 'view') {
                        handleFormChange('latitude', pos[0].toFixed(6));
                        handleFormChange('longitude', pos[1].toFixed(6));
                      }
                    }}
                    height={550}
                  />
                ) : (
                  <Box sx={{ height: 550, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', border: '2px dashed #d1d5db', borderRadius: 2 }}>
                    <Stack alignItems="center" spacing={2}>
                      <LocationIcon sx={{ fontSize: 56, color: "#9ca3af" }} />
                      <Typography variant="h6" color="#6b7280" textAlign="center" fontWeight={500}>
                        No coordinates available
                        {dialogMode !== 'view' && (
                          <>
                            <br />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Add latitude and longitude to see location
                            </Typography>
                          </>
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {(formData.latitude && formData.longitude) && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: '#eff6ff', borderRadius: 2, border: '1px solid #3b82f6' }}>
                    <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 500 }}>
                      <LocationIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                      <strong>Coordinates:</strong> {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: '#fafafa' }}>
          <Button onClick={handleCloseDialog} variant="outlined" startIcon={<CancelIcon />} sx={{ borderColor: '#6b7280', color: '#6b7280' }}>
            Cancel
          </Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSaveRecord} variant="contained" startIcon={<SaveIcon />} sx={{ backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" } }}>
              Save Changes
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Map Selection Dialog */}
      <Dialog open={mapDialogOpen} onClose={closeMapDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MapIcon />
            <Typography>Select Location</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click on the map to select coordinates for your GLN location
          </Typography>
          <MapPicker position={mapPosition} onPositionChange={setMapPosition} height={400} />
          {mapPosition && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Selected Coordinates:</strong><br />
                Latitude: {mapPosition[0].toFixed(6)}<br />
                Longitude: {mapPosition[1].toFixed(6)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMapDialog} startIcon={<CancelIcon />}>Cancel</Button>
          <Button onClick={getCurrentLocation} startIcon={<MyLocationIcon />} variant="outlined">Use Current Location</Button>
          <Button onClick={handleMapConfirm} startIcon={<CheckIcon />} variant="contained" disabled={!mapPosition} sx={{ backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" } }}>
            Confirm Location
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={handleCancelDeactivate} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <DeactivateIcon sx={{ fontSize: 28, color: "#dc2626" }} />
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#111827" }}>
                Deactivate GLN Record
              </Typography>
              {recordToDeactivate && (
                <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                  GLN ID: {recordToDeactivate.gln_id}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {recordToDeactivate && (
            <Stack spacing={2}>
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <AlertTitle>Warning: This action will deactivate the record</AlertTitle>
                This will change the status to inactive and prevent any further operations.
              </Alert>

              <Box sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Record Details:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • GLN ID: <strong>{recordToDeactivate.gln_id}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Code: <strong>{recordToDeactivate.generated_code}</strong>
                </Typography>
                {recordToDeactivate.facility_name && (
                  <Typography variant="body2" color="text.secondary">
                    • Facility: <strong>{recordToDeactivate.facility_name}</strong>
                  </Typography>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: '#fafafa', borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={handleCancelDeactivate} variant="outlined">Cancel</Button>
          <Button onClick={handleConfirmDeactivate} variant="contained" sx={{ backgroundColor: "#dc2626", "&:hover": { backgroundColor: "#b91c1c" } }}>
            Confirm Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MyGLNsView;